if (!customElements.get('product-lightbox')) {
  const DESKTOP_MIN_WIDTH = 1100;
  const OPEN_DURATION = 250;
  const SLIDE_DURATION = 500;

  class ProductLightbox extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.trigger = null;
      this.scrollPosition = 0;
      this.onKeydown = this.handleKeydown.bind(this);
      this.onClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      // Reparent to <body> so no ancestor transform, overflow or z-index can trap
      // the overlay. Both gallery scripts are deferred, so the markup is complete.
      if (this.parentElement !== document.body) {
        // A section re-render in the theme editor leaves the previous overlay behind
        document.querySelectorAll('product-lightbox').forEach((el) => {
          if (el !== this) el.remove();
        });

        document.body.appendChild(this);
        return;
      }

      this.dialog = this.querySelector('.product-lightbox__dialog');
      this.slider = this.querySelector('.product-lightbox__slider');
      this.wrapper = this.querySelector('.product-lightbox__slider .swiper-wrapper');
      this.slides = this.wrapper ? Array.from(this.wrapper.children) : [];

      // Slides for other variants are flagged in Liquid; keep them aside so the order matches the inline gallery
      if (this.wrapper) {
        this.wrapper.replaceChildren(...this.slides.filter((slide) => !slide.hasAttribute('data-filtered')));
        this.slides.forEach((slide) => slide.removeAttribute('data-filtered'));
      }

      this.images = Array.from(this.querySelectorAll('.product-lightbox__image'));
      this.closeButton = this.querySelector('.product-lightbox__close');
      this.thumb = this.querySelector('.product-lightbox__thumb');
      this.track = this.querySelector('.product-lightbox__track');

      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      // The reparenting move fires this too
      if (this.isConnected) return;

      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      document.body.classList.remove('product-lightbox-open');
      document.body.style.top = '';
    }

    // Called by product-slider on variant change with the same predicate it applied inline
    filterSlides(isShown) {
      if (!this.wrapper) return;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.wrapper.replaceChildren(...this.slides.filter(isShown));
      this.images = Array.from(this.querySelectorAll('.product-lightbox__image'));
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get isOpen() {
      return !this.hidden;
    }

    open(index = 0, trigger = null) {
      if (this.isOpen) return;

      this.trigger = trigger;
      this.hidden = false;
      this.lockScroll();
      this.initSwiper();

      if (this.swiper) {
        this.swiper.slideTo(index, 0);
        // A hidden swiper still reads as in-viewport, so it would swallow the page's arrow keys
        this.swiper.keyboard.enable();
      }

      this.promoteAround(index);
      this.sizeTrack();

      document.addEventListener('keydown', this.onKeydown);

      // Flush the closed state so the transition still runs, then reveal in the same tick
      void this.offsetWidth;
      this.classList.add('is-open');

      if (this.closeButton) this.closeButton.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;

      const index = this.swiper ? this.swiper.activeIndex : 0;

      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.swiper) this.swiper.keyboard.disable();

      // Re-sync the inline gallery only once we're hidden, so it can't be seen jumping
      const finish = () => {
        this.hidden = true;
        this.unlockScroll();

        if (this.trigger) this.trigger.focus({ preventScroll: true });
        this.trigger = null;

        this.dispatchEvent(new CustomEvent('product-lightbox:close', { detail: { index } }));
      };

      if (this.prefersReducedMotion) {
        finish();
      } else {
        setTimeout(finish, OPEN_DURATION);
      }
    }

    initSwiper() {
      if (this.swiper || typeof Swiper === 'undefined' || !this.slider) return;

      this.swiper = new Swiper(this.slider, {
        slidesPerView: 1,
        speed: this.prefersReducedMotion ? 0 : SLIDE_DURATION,
        effect: 'coverflow',
        coverflowEffect: {
          // Scale resolves to 1 - (1 - scale) * distance, so the centred slide stays at full size
          rotate: 0,
          stretch: 0,
          depth: 0,
          scale: 0.75,
          modifier: 1,
          slideShadows: false,
        },
        navigation: {
          prevEl: this.querySelector('.product-lightbox__arrow--prev'),
          nextEl: this.querySelector('.product-lightbox__arrow--next'),
        },
        pagination: {
          el: this.querySelector('.product-lightbox__pagination'),
          clickable: true,
          bulletClass: 'product-lightbox__bullet',
          bulletActiveClass: 'is-active',
        },
        keyboard: { enabled: false },
        a11y: {
          prevSlideMessage: this.dataset.prevLabel,
          nextSlideMessage: this.dataset.nextLabel,
        },
        on: {
          slideChange: (swiper) => {
            this.promoteAround(swiper.activeIndex);
            this.sizeTrack();
          },
          resize: () => this.sizeTrack(),
          // progress tracks the drag too, so the thumb follows a finger mid-swipe
          progress: (swiper, progress) => this.moveThumb(progress),
          setTransition: (swiper, duration) => {
            if (this.thumb) this.thumb.style.transitionDuration = `${duration}ms`;
            if (this.track) this.track.style.transitionDuration = `${duration}ms`;
          },
        },
      });

      if (this.thumb) {
        this.thumb.style.width = `${100 / this.swiper.slides.length}%`;
        this.moveThumb(this.swiper.progress);
      }
    }

    moveThumb(progress) {
      const steps = this.swiper ? this.swiper.slides.length - 1 : 0;

      if (!this.thumb || steps < 1) return;

      const clamped = Math.min(Math.max(progress, 0), 1);
      // One step equals one thumb width, so the offset is a multiple of 100%
      this.thumb.style.transform = `translateX(${clamped * steps * 100}%)`;
    }

    sizeTrack() {
      if (!this.track || !this.swiper) return;

      // Desktop frames every image at 4:5, so CSS owns the track width there
      if (window.innerWidth >= DESKTOP_MIN_WIDTH) {
        this.track.style.width = '';
        return;
      }

      const width = this.measureImage(this.images[this.swiper.activeIndex]);

      if (width) this.track.style.width = `${Math.round(width)}px`;
    }

    // object-fit: contain, so the painted image can be narrower than its box
    measureImage(img) {
      if (!img) return 0;

      const boxWidth = img.offsetWidth;
      const boxHeight = img.offsetHeight;
      const naturalRatio = img.naturalWidth / img.naturalHeight;
      // Pre-load the attributes carry the same ratio, so the track is right on the first frame
      const attrRatio = parseFloat(img.getAttribute('width')) / parseFloat(img.getAttribute('height'));
      const ratio = naturalRatio || attrRatio;

      if (!boxWidth || !boxHeight || !ratio) return 0;

      return Math.min(boxWidth, boxHeight * ratio);
    }

    promoteAround(index) {
      [index - 1, index, index + 1].forEach((i) => this.promoteImage(this.images[i]));
    }

    promoteImage(img) {
      if (!img || img.dataset.promoted) return;

      const preview =
        window.innerWidth >= DESKTOP_MIN_WIDTH ? img.dataset.previewDesktop : img.dataset.previewMobile;

      img.dataset.promoted = 'true';

      const upgrade = () => {
        if (img.dataset.sizes) img.sizes = img.dataset.sizes;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        this.sizeTrack();
      };

      if (!preview) {
        upgrade();
        return;
      }

      // Full res only once the cached preview has painted, so the first frame is never blank
      img.addEventListener('load', upgrade, { once: true });
      img.addEventListener('error', upgrade, { once: true });
      img.src = preview;
    }

    handleClick(event) {
      // Swiper only preventDefaults a post-drag click, it does not always stop propagation
      if (event.defaultPrevented) return;
      if (this.swiper && this.swiper.allowClick === false) return;

      const interactive = event.target.closest(
        '.product-lightbox__image, .product-lightbox__arrow, .product-lightbox__pagination'
      );

      if (interactive) return;

      this.close();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      this.scrollPosition = window.scrollY;
      // Offset before the class lands, otherwise the fixed body flashes at the top
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.classList.add('product-lightbox-open');
    }

    unlockScroll() {
      document.body.classList.remove('product-lightbox-open');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollPosition);
    }
  }

  customElements.define('product-lightbox', ProductLightbox);
}
